import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../common/ConfirmModal";
import { listComments, addComment, editComment, deleteComment, resolveComment } from "../../api/discussionApi";
import "./discussionPanel.css";

/**
 * The discussion on the selected document.
 *
 * <b>Threads are one level deep.</b> A root comment and its replies, and that is the whole shape. Deeper
 * nesting turns a side panel into something nobody can follow, and it makes "has this been dealt with"
 * unanswerable — resolution belongs to the thread, so it lives on the root.
 *
 * <b>Resolved threads collapse; they do not disappear.</b> The question and its answer are the record. A
 * resolved thread shows its root and a count, and opens on a click.
 *
 * <b>The mention suggestions come from the thread, not from a directory.</b> The staff directory is
 * deliberately admin-gated — a list of every colleague's login and e-mail is a real disclosure — and this
 * panel is an ordinary user's screen. So the type-ahead offers the people already talking on this document,
 * which is who a reply is nearly always addressed to, and anything else can be typed in full. A name the
 * server cannot resolve to an account simply raises no notification; it stays in the text as written.
 *
 * Props: dbName, archiveNo, documentId (provenance for new comments), token, onCountChange
 */
export default function DiscussionPanel({ dbName, archiveNo, documentId, token, onCountChange }) {
  const { t } = useTranslation();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);        // root comment id being replied to
  const [replyDraft, setReplyDraft] = useState("");
  const [editing, setEditing] = useState(null);        // { id, body }
  const [expanded, setExpanded] = useState({});        // rootId -> true, for resolved threads opened by hand
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [suggest, setSuggest] = useState(null);        // { target:'new'|'reply', matches:[], token:string }

  const composerRef = useRef(null);
  const replyRef = useRef(null);

  const canLoad = !!(dbName && archiveNo && token);

  const load = useCallback(async () => {
    if (!canLoad) { setThreads([]); return; }
    setLoading(true);
    setError("");
    try {
      const data = await listComments(token, dbName, archiveNo);
      const list = data?.threads || [];
      setThreads(list);
      if (onCountChange) onCountChange(data?.total || 0);
    } catch (e) {
      // A licence that does not include content-governance answers 403 here. That is a configuration
      // fact rather than a failure, so it reads as an explanation and not as an error.
      setError(e?.status === 403
        ? t("discussion.notLicensed", { defaultValue: "Discussion is part of content governance, which is not enabled." })
        : (e?.message || t("discussion.loadFailed", { defaultValue: "The discussion could not be loaded." })));
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [canLoad, token, dbName, archiveNo, onCountChange, t]);

  useEffect(() => { load(); }, [load]);

  // Everyone who has said something here — the pool the @ type-ahead offers.
  const participants = useMemo(() => {
    const names = new Set();
    for (const th of threads) {
      if (th?.root?.createdBy) names.add(th.root.createdBy);
      for (const r of th?.replies || []) if (r?.createdBy) names.add(r.createdBy);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [threads]);

  // The word being typed after an "@", if the caret is inside one.
  const mentionTokenAt = (text, caret) => {
    const upto = text.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at < 0) return null;
    if (at > 0 && !/\s/.test(upto[at - 1])) return null;     // part of an e-mail, not a mention
    const word = upto.slice(at + 1);
    if (/\s/.test(word)) return null;                        // the mention ended a word ago
    return { at, word };
  };

  const onDraftChange = (target, value, caret) => {
    if (target === "new") setDraft(value); else setReplyDraft(value);
    const tok = mentionTokenAt(value, caret);
    if (!tok) { setSuggest(null); return; }
    const matches = participants.filter(
      (p) => tok.word.length === 0 || p.toLowerCase().includes(tok.word.toLowerCase()),
    ).slice(0, 6);
    setSuggest(matches.length ? { target, matches, at: tok.at, word: tok.word } : null);
  };

  const applySuggestion = (name) => {
    if (!suggest) return;
    const isNew = suggest.target === "new";
    const text = isNew ? draft : replyDraft;
    const next = text.slice(0, suggest.at) + "@" + name + " " + text.slice(suggest.at + 1 + suggest.word.length);
    if (isNew) setDraft(next); else setReplyDraft(next);
    setSuggest(null);
    const el = isNew ? composerRef.current : replyRef.current;
    if (el) el.focus();
  };

  // Which of the suggestible names the text actually names. The server applies the same rule and is the
  // one that decides; sending the list is a convenience, not an authority.
  const mentionsIn = (text) =>
    participants.filter((p) => text.toLowerCase().includes("@" + p.toLowerCase()));

  const post = async (body, parentCommentId) => {
    const text = (body || "").trim();
    if (!text || busy) return;
    setBusy(true);
    setError("");
    try {
      await addComment(token, {
        databaseName: dbName,
        archiveNo,
        body: text,
        parentCommentId: parentCommentId || undefined,
        documentId: documentId || undefined,
        mentions: mentionsIn(text),
      });
      if (parentCommentId) { setReplyDraft(""); setReplyTo(null); } else { setDraft(""); }
      setSuggest(null);
      await load();
    } catch (e) {
      setError(e?.code === "BODY_TOO_LONG"
        ? t("discussion.tooLong", { defaultValue: "A comment can be at most 2000 characters." })
        : (e?.message || t("discussion.postFailed", { defaultValue: "The comment could not be posted." })));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing || busy) return;
    const text = (editing.body || "").trim();
    if (!text) return;
    setBusy(true);
    try {
      await editComment(token, editing.id, { databaseName: dbName, body: text });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.message || t("discussion.editFailed", { defaultValue: "The comment could not be edited." }));
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target) return;
    setBusy(true);
    try {
      await deleteComment(token, target.id, dbName);
      await load();
    } catch (e) {
      setError(e?.message || t("discussion.deleteFailed", { defaultValue: "The comment could not be removed." }));
    } finally { setBusy(false); }
  };

  const toggleResolved = async (root) => {
    setBusy(true);
    try {
      await resolveComment(token, root.id, { databaseName: dbName, resolved: !root.isResolved });
      await load();
    } catch (e) {
      setError(e?.message || t("discussion.resolveFailed", { defaultValue: "The thread could not be updated." }));
    } finally { setBusy(false); }
  };

  const when = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
  };

  // Renders @name as an emphasised span without letting anything else in the body be interpreted as
  // markup — the text is inserted as text, only the mention spans are elements.
  const renderBody = (text) => {
    const out = [];
    const re = /(^|\s)@([^\s@]{1,200})/g;
    let last = 0, m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      out.push(m[1]);
      out.push(<span className="disc-mention" key={`${m.index}-${m[2]}`}>@{m[2]}</span>);
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  const Comment = ({ c, isRoot }) => (
    <div className={`disc-comment${isRoot ? " root" : " reply"}`}>
      <div className="disc-comment-head">
        <span className="disc-author">{c.createdBy || t("discussion.unknownAuthor", { defaultValue: "Unknown" })}</span>
        <span className="disc-when" title={when(c.createdAt)}>{when(c.createdAt)}</span>
        {c.editedAt && (
          <span className="disc-edited" title={when(c.editedAt)}>
            {t("discussion.edited", { defaultValue: "edited" })}
          </span>
        )}
      </div>

      {c.isDeleted ? (
        <div className="disc-body deleted">{t("discussion.removed", { defaultValue: "This comment was removed." })}</div>
      ) : editing && editing.id === c.id ? (
        <div className="disc-edit">
          <textarea
            className="disc-textarea"
            value={editing.body}
            maxLength={2000}
            onChange={(e) => setEditing({ ...editing, body: e.target.value })}
          />
          <div className="disc-edit-actions">
            <button type="button" className="disc-btn primary" disabled={busy} onClick={saveEdit}>
              {t("common.save", { defaultValue: "Save" })}
            </button>
            <button type="button" className="disc-btn" onClick={() => setEditing(null)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </div>
      ) : (
        <div className="disc-body">{renderBody(c.body || "")}</div>
      )}

      {!c.isDeleted && !(editing && editing.id === c.id) && (
        <div className="disc-comment-actions">
          {isRoot && (
            <button type="button" className="disc-link" onClick={() => { setReplyTo(c.id); setReplyDraft(""); }}>
              {t("discussion.reply", { defaultValue: "Reply" })}
            </button>
          )}
          {c.canEdit && (
            <button type="button" className="disc-link" onClick={() => setEditing({ id: c.id, body: c.body || "" })}>
              {t("common.edit", { defaultValue: "Edit" })}
            </button>
          )}
          {c.canDelete && (
            <button type="button" className="disc-link danger" onClick={() => setConfirmDelete(c)}>
              {t("common.delete", { defaultValue: "Delete" })}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const Suggestions = ({ target }) =>
    suggest && suggest.target === target ? (
      <div className="disc-suggest" role="listbox">
        {suggest.matches.map((name) => (
          <button type="button" key={name} className="disc-suggest-item" onClick={() => applySuggestion(name)}>
            @{name}
          </button>
        ))}
      </div>
    ) : null;

  if (!canLoad) return null;

  return (
    <div className="dp-section disc-panel">
      <div className="dp-section-title">
        {t("discussion.title", { defaultValue: "Discussion" })}
      </div>

      {/* Composer first: the panel is opened far more often to say something than to read back. */}
      <div className="disc-composer">
        <textarea
          ref={composerRef}
          className="disc-textarea"
          placeholder={t("discussion.placeholder", { defaultValue: "Add a comment. Type @ to mention someone in this thread." })}
          value={draft}
          maxLength={2000}
          onChange={(e) => onDraftChange("new", e.target.value, e.target.selectionStart)}
        />
        <Suggestions target="new" />
        <div className="disc-composer-actions">
          <span className="disc-count">{draft.length}/2000</span>
          <button type="button" className="disc-btn primary" disabled={busy || !draft.trim()} onClick={() => post(draft, null)}>
            {t("discussion.post", { defaultValue: "Comment" })}
          </button>
        </div>
      </div>

      {error && <div className="disc-error">{error}</div>}
      {loading && <div className="disc-muted">{t("common.loading", { defaultValue: "Loading…" })}</div>}

      {!loading && threads.length === 0 && !error && (
        <div className="disc-muted">{t("discussion.empty", { defaultValue: "No discussion on this document yet." })}</div>
      )}

      {threads.map((th) => {
        const root = th.root;
        const replies = th.replies || [];
        const collapsed = root.isResolved && !expanded[root.id];
        return (
          <div className={`disc-thread${root.isResolved ? " resolved" : ""}`} key={root.id}>
            {root.isResolved && (
              <div className="disc-resolved-bar">
                <span>
                  {t("discussion.resolvedBy", { defaultValue: "Resolved by {{user}}", user: root.resolvedBy || "—" })}
                </span>
                <button type="button" className="disc-link" onClick={() => toggleResolved(root)} disabled={busy}>
                  {t("discussion.reopen", { defaultValue: "Reopen" })}
                </button>
              </div>
            )}

            <Comment c={root} isRoot />

            {collapsed ? (
              replies.length > 0 && (
                <button type="button" className="disc-link disc-expand" onClick={() => setExpanded({ ...expanded, [root.id]: true })}>
                  {t("discussion.showReplies", { defaultValue: "{{count}} replies", count: replies.length })}
                </button>
              )
            ) : (
              <>
                {replies.map((r) => <Comment c={r} key={r.id} isRoot={false} />)}

                {replyTo === root.id ? (
                  <div className="disc-reply-box">
                    <textarea
                      ref={replyRef}
                      className="disc-textarea"
                      placeholder={t("discussion.replyPlaceholder", { defaultValue: "Write a reply…" })}
                      value={replyDraft}
                      maxLength={2000}
                      autoFocus
                      onChange={(e) => onDraftChange("reply", e.target.value, e.target.selectionStart)}
                    />
                    <Suggestions target="reply" />
                    <div className="disc-composer-actions">
                      <button type="button" className="disc-btn primary" disabled={busy || !replyDraft.trim()} onClick={() => post(replyDraft, root.id)}>
                        {t("discussion.postReply", { defaultValue: "Reply" })}
                      </button>
                      <button type="button" className="disc-btn" onClick={() => { setReplyTo(null); setSuggest(null); }}>
                        {t("common.cancel", { defaultValue: "Cancel" })}
                      </button>
                    </div>
                  </div>
                ) : (
                  !root.isResolved && (
                    <button type="button" className="disc-link disc-resolve" onClick={() => toggleResolved(root)} disabled={busy}>
                      {t("discussion.resolve", { defaultValue: "Mark resolved" })}
                    </button>
                  )
                )}
              </>
            )}
          </div>
        );
      })}

      <ConfirmModal
        open={!!confirmDelete}
        title={t("discussion.deleteTitle", { defaultValue: "Remove this comment?" })}
        body={t("discussion.deleteBody", {
          defaultValue: "It stops being shown to everyone. The record is kept, so a discussion on a document under legal hold is not lost.",
        })}
        variant="danger"
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
